import { Link } from "react-router-dom";
import { Facebook, Instagram } from "lucide-react";
import { CONTACTS, MOVEMENT, SOCIALS, waLink } from "../data/content";
import { WhatsAppIcon } from "./WhatsAppCTA";
import { useSiteContent } from "../hooks/useSiteContent";

export const Footer = () => {
  const { settings } = useSiteContent();
  const instagramUrl = settings?.instagram?.profileUrl;

  return (
  <footer data-testid="site-footer" className="bg-navy text-bone">
    <div className="mx-auto max-w-[1400px] px-5 pb-10 pt-16 md:px-10 md:pt-24">
      <div className="grid gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Logo Progetto Città" className="h-16 w-auto rounded-full bg-bone p-1" />
            <p className="font-oswald text-xl font-semibold uppercase leading-tight tracking-tight">
              {MOVEMENT.type}
              <br />
              <span className="text-orange">{MOVEMENT.name}</span>
            </p>
          </div>
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-bone/70">
            Un movimento di cittadini per Trapani ed Erice. {MOVEMENT.motto}.
          </p>
        </div>

        <div className="md:col-span-3">
          <h3 className="font-oswald text-sm uppercase tracking-[0.25em] text-bone/60">Esplora</h3>
          <ul className="mt-5 space-y-3">
            {[
              { to: "/", label: "Home" },
              { to: "/chi-siamo", label: "Chi Siamo" },
              { to: "/membri", label: "Membri" },
              { to: "/eventi", label: "Eventi" },
              { to: "/adesione", label: "Aderisci" },
              { to: "/contatti", label: "Contatti" },
            ].map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(" ", "-")}`}
                  className="text-sm text-bone/80 transition-colors duration-300 hover:text-orange"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <h3 className="font-oswald text-sm uppercase tracking-[0.25em] text-bone/60">Contatti</h3>
          <ul className="mt-5 space-y-3 text-sm text-bone/80">
            <li>
              <a data-testid="footer-email-link" href={`mailto:${CONTACTS.email}`} className="transition-colors duration-300 hover:text-orange">
                {CONTACTS.email}
              </a>
            </li>
            <li data-testid="footer-phone">{CONTACTS.phoneDisplay}</li>
            <li>
              <a
                data-testid="footer-whatsapp-link"
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 transition-colors duration-300 hover:text-orange"
              >
                <WhatsAppIcon className="h-4 w-4" /> WhatsApp
              </a>
            </li>
            <li>
              <a
                data-testid="footer-whatsapp-channel-link"
                href={SOCIALS.whatsappChannel}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 transition-colors duration-300 hover:text-orange"
              >
                <WhatsAppIcon className="h-4 w-4" /> Canale WhatsApp
              </a>
            </li>
          </ul>
          <h3 className="mt-8 font-oswald text-sm uppercase tracking-[0.25em] text-bone/60">Seguici</h3>
          <div className="mt-4 flex items-center gap-3">
            <a
              data-testid="footer-facebook-link"
              href={SOCIALS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Pagina Facebook di Progetto Città"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-bone/30 text-bone/80 transition-[background-color,color,border-color] duration-300 hover:border-orange hover:bg-orange hover:text-white"
            >
              <Facebook className="h-5 w-5" />
            </a>
            {instagramUrl && (
              <a
                data-testid="footer-instagram-link"
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Pagina Instagram di Progetto Città"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-bone/30 text-bone/80 transition-[background-color,color,border-color] duration-300 hover:border-orange hover:bg-orange hover:text-white"
              >
                <Instagram className="h-5 w-5" />
              </a>
            )}
          </div>
        </div>
      </div>

      <p aria-hidden="true" className="text-outline-bone mt-16 select-none font-oswald text-[13.5vw] font-bold uppercase leading-[0.85] tracking-tight md:text-[10vw]">
        Progetto Città
      </p>

      <div className="mt-10 flex flex-col gap-2 border-t border-bone/20 pt-6 text-xs text-bone/50 md:flex-row md:items-center md:justify-between">
        <p data-testid="footer-copyright">© 2026 {MOVEMENT.type} {MOVEMENT.name}. Tutti i diritti riservati.</p>
        <p>{MOVEMENT.motto}</p>
      </div>
    </div>
  </footer>
  );
};
